/**
 * APX WorkCenter — SendNotificationModal
 * Sends in-hub notifications only. No outbound email to personal addresses.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// SOP: Static non-bookkeeper recipients. Bookkeepers added dynamically from DB inside component.
const STATIC_NOTIFICATION_RECIPIENTS = [
  { label: "All Team", value: "role:all" },
  { label: "All Bkkprs", value: "role:bookkeeper" },
  { label: "All Controllers", value: "role:controller" },
  { label: "Jany Lopez — Bkkpr Mgr", value: "email:lopez.jany@gmail.com" },
  { label: "Gus — Controller", value: "email:gus@bbnfi.com" },
  { label: "Amy — Controller", value: "email:amybyfield@gmail.com" },
  { label: "Mr. B — Director", value: "email:byjosiah@gmail.com" },
  { label: "Jasmin — Tax Division", value: "email:jasminsilva91@gmail.com" },
  { label: "Grecia S. — Asst Controller", value: "email:grecia.ssoto13@gmail.com" },
  { label: "Anabel D. — Asst Controller", value: "email:anabelldiazcoronado@msn.com" },
  { label: "Diana G. — Tax Division", value: "email:team@mfisinc.com" },
  { label: "Luna J. — Tax Division", value: "email:htminfo@gmail.com" },
];

const TYPES = [
  { label: "Task", value: "task" },
  { label: "Alert", value: "alert" },
  { label: "Info", value: "info" },
  { label: "P&L Request", value: "pl_request" },
];

interface Props {
  onClose: () => void;
  defaultRecipient?: string;
  defaultType?: string;
  defaultTitle?: string;
  defaultMessage?: string;
}

export default function SendNotificationModal({
  onClose,
  defaultRecipient = "",
  defaultType = "task",
  defaultTitle = "",
  defaultMessage = "",
}: Props) {
  const { user } = useAuth();
  // SOP: Live bookkeeper list from DB — auto-includes new bookkeepers added via Team & Access
  const { data: liveBookkeepers = [] } = trpc.team.listBookkeepers.useQuery(undefined, { refetchInterval: 60_000, staleTime: 30_000 });
  const RECIPIENTS = [
    ...STATIC_NOTIFICATION_RECIPIENTS,
    ...liveBookkeepers
      .filter(bk => bk.role === "bookkeeper" || bk.role === "bookkeeper_supervisor")
      .map(bk => ({ label: `${bk.name} — Bkkpr`, value: `email:${bk.email}` })),
  ];
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState(defaultTitle);
  const [message, setMessage] = useState(defaultMessage);

  const send = trpc.notifications.send.useMutation({
    onSuccess: () => {
      toast.success("Notification sent", {
        description: `In-hub notification delivered to ${RECIPIENTS.find(r => r.value === recipient)?.label ?? recipient}`,
      });
      onClose();
    },
    onError: (err) => toast.error("Failed to send", { description: err.message }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipient || !title || !message) return;
    const isRole = recipient.startsWith("role:");
    send.mutate({
      senderName: user?.name ?? "Unknown",
      senderRole: user?.role ?? "controller",
      recipientEmail: isRole ? undefined : recipient.replace("email:", ""),
      recipientRole: isRole ? recipient.replace("role:", "") : undefined,
      title,
      message,
      type: type as "task" | "alert" | "info" | "pl_request" | "file",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "oklch(0 0 0 / 60%)" }}>
      <div
        className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
        style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-[oklch(0.08_0.01_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Send Notification
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[oklch(0.35_0.02_240)] hover:text-[oklch(0.18_0.04_240)] transition-colors"
            style={{ background: "oklch(1 0 0 / 6%)" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient */}
          <div>
            <label className="block text-sm font-medium text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-1.5">
              Send To
            </label>
            <select
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              required
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] outline-none"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
            >
              <option value="">Select recipient…</option>
              {RECIPIENTS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-1.5">
              Type
            </label>
            <div className="flex gap-2 flex-wrap">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150"
                  style={{
                    background: type === t.value ? "oklch(0.72 0.14 75)" : "oklch(0.92 0.01 240)",
                    border: `1px solid ${type === t.value ? "oklch(0.60 0.14 75)" : "oklch(0.75 0.02 240)"}`,
                    color: type === t.value ? "oklch(0.12 0.04 240)" : "oklch(0.25 0.04 240)",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-1.5">
              Subject
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. P&L ready for Sunrise Café"
              required
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-[oklch(0.08_0.01_240)] uppercase tracking-wider mb-1.5">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message…"
              required
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.35_0.02_240)] outline-none resize-none"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-[oklch(0.08_0.01_240)] transition-colors"
              style={{ background: "oklch(0.97 0.01 220)", border: "1px solid oklch(1 0 0 / 10%)" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={send.isPending}
              className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
              style={{
                background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))",
                color: "oklch(0.12 0.04 240)",
                opacity: send.isPending ? 0.7 : 1,
              }}
            >
              {send.isPending ? "Sending…" : "Send Notification"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
